import {useMemo} from 'react'

import {AnalyticsDataContextProvider} from '../../analytics/AnalyticsDataContextProvider'
import {CommonConfigurationContext} from '../../configuration/CommonConfigurationContext'
import {ConfigurationContext} from '../../configuration/ConfigurationContext'
import {OrderProcessFormProvider} from '../../form/OrderProcessFormProvider'
import {OrderDto, OrderStatus} from '../types'

import {
  buildOrderChangeAnalyticsData,
  OrderChangeAnalyticsData
} from './buildOrderChangeAnalyticsData'
import {OrderChangeContextProvider} from './context/OrderChangeContextProvider'
import {OrderChangeFeatureToggles} from './featureToggles'
import {buildConfiguration as buildFieldConfiguration} from './fields/buildConfiguration'
import {buildForm} from './fields/buildForm'
import {buildWorkingForm} from './fields/buildWorkingForm'
import {mapOrderChangeOptions} from './options/mapOrderChangeOptions'
import {OrderChangeOptionsDto} from './options/types'
import {buildConfiguration as buildOrderConfirmationConfiguration} from './orderConfirmation/configuration/buildConfiguration'
import {OrderConfirmationContext} from './orderConfirmation/context/OrderConfirmationContext'

export type OrderChangeProcessProviderProps = React.PropsWithChildren & {
  options: OrderChangeOptionsDto
  order: OrderDto
  featureToggles: OrderChangeFeatureToggles
  respectStartedOrderSpacingRestrictions?: boolean
  /**
   * Refresh interval in milliseconds for changeable fields state updates (default: 60000ms / 60s).
   * Used for time-based field state transitions (autoConfirmable → requireDispatchConfirmation → notChangeable).
   *
   * @remarks
   * TODO: Consider optimization - instead of fixed interval polling, calculate exact time until next
   * state transition and use setTimeout (similar to OrderCancellationContextProvider approach).
   * This would eliminate unnecessary polling when no state changes are imminent.
   *
   * @see OrderCancellationContextProvider for reference implementation of smart timeout scheduling
   */
  changeableFieldsRefreshIntervalMs?: number
}

/**
 * Order statuses that allow automatic adjustment of load spacing and time on job site fields.
 *
 * Auto-adjustment is enabled only for orders in early lifecycle stages where scheduling
 * changes are expected and won't disrupt logistics (Pending, Reserved, Confirmed).
 *
 * For later stages (Shipped, Delivered, etc.), manual adjustment is required to prevent
 * unintended changes to already-scheduled deliveries.
 *
 * @optimization Consider moving this logic to backend API to:
 * - Centralize business rules in one place
 * - Reduce duplication between Order Change and Order Intake
 * - Allow server-side validation before persisting changes
 * - Make it easier to adjust rules without frontend deployments
 */
const LOAD_SPACING_AUTO_ADJUST_ALLOWED_STATUSES = [
  OrderStatus.Pending,
  OrderStatus.Reserved,
  OrderStatus.Confirmed
]

export const OrderChangeProcessProvider: React.FC<OrderChangeProcessProviderProps> = ({
  children,
  options,
  order,
  featureToggles,
  respectStartedOrderSpacingRestrictions = false,
  changeableFieldsRefreshIntervalMs
}) => {
  const inputProps = useMemo(
    () => ({
      confirmationPinRequired: order.confirmationPinRequired ?? false,
      enableLoadSpacingAutoAdjustment: LOAD_SPACING_AUTO_ADJUST_ALLOWED_STATUSES.includes(
        order.orderStatus
      )
    }),
    [order.confirmationPinRequired, order.orderStatus]
  )

  const orderProcessOptions = useMemo(() => mapOrderChangeOptions(options), [options])

  const fieldsConfiguration = useMemo(
    () =>
      buildFieldConfiguration(
        order,
        {
          inputProps,
          options: orderProcessOptions,
          featureToggles: {
            enableConfirmationPin: featureToggles.enableConfirmationPin,
            enableSlotManagement: featureToggles.enableSlotManagementForRmcOrderManagement
          }
        },
        options.changeableFields,
        respectStartedOrderSpacingRestrictions
      ),
    [
      featureToggles.enableConfirmationPin,
      featureToggles.enableSlotManagementForRmcOrderManagement,
      inputProps,
      orderProcessOptions,
      order,
      options.changeableFields,
      respectStartedOrderSpacingRestrictions
    ]
  )

  const orderConfirmationConfiguration = useMemo(
    () => buildOrderConfirmationConfiguration(options.constraints.quantity),
    [options.constraints.quantity]
  )

  const form = useMemo(
    () =>
      buildForm({
        order,
        configuration: fieldsConfiguration,
        inputProps
      }),
    [fieldsConfiguration, inputProps, order]
  )

  // For an unconfirmed UE/CC order with a non-zero offset, seed the order time with the rescheduled
  // `variationCodeAdjustedTime` as the reactive working value while `form` stays the dirty baseline.
  // `buildWorkingForm` returns the same `form` reference when no override applies, so normal orders
  // keep the exact prior defaultValues-only behavior (no reactive `values` engaged).
  const workingForm = useMemo(() => buildWorkingForm(order, form), [order, form])
  const hasWorkingOverride = workingForm !== form

  const analyticsData = useMemo(() => buildOrderChangeAnalyticsData(order), [order])

  return (
    <CommonConfigurationContext.Provider value={fieldsConfiguration}>
      <ConfigurationContext.Provider value={fieldsConfiguration}>
        <OrderConfirmationContext.Provider value={orderConfirmationConfiguration}>
          <AnalyticsDataContextProvider<OrderChangeAnalyticsData> analyticsData={analyticsData}>
            <OrderProcessFormProvider
              country={order.orgUnitCountryId}
              defaultValues={form}
              values={hasWorkingOverride ? workingForm : undefined}
              keepDefaultValues={hasWorkingOverride}
              isOrderChangeProcess
            >
              <OrderChangeContextProvider
                order={order}
                changeableFieldsOptions={options.changeableFields}
                refreshIntervalMs={changeableFieldsRefreshIntervalMs}
                featureToggles={featureToggles}
              >
                {children}
              </OrderChangeContextProvider>
            </OrderProcessFormProvider>
          </AnalyticsDataContextProvider>
        </OrderConfirmationContext.Provider>
      </ConfigurationContext.Provider>
    </CommonConfigurationContext.Provider>
  )
}
